import {useCallback, useState} from 'react'
import {isLoggedIn} from '@github-ui/client-env'
import {updateDockedPanelWidth} from './update-docked-panel-width'

export type UseDockedPanelWidthControlParams = {
  serverWidth: number | null | undefined
  minWidth: number
  maxWidth: number
  owner: string | undefined
  repo: string | undefined
  pullNumber: number | undefined
}

export type UseDockedPanelWidthControlResult = {
  currentWidth: number | undefined
  onResizeEnd: (newWidth: number) => void
}

function clamp(value: number | null | undefined, min: number, max: number): number | undefined {
  return value != null && value >= min && value <= max ? value : undefined
}

/**
 * Manages the controlled `currentWidth` for the diff sidebar.
 *
 * The server value seeds initial state and re-syncs whenever it changes
 * (e.g. on refetch). Resizes update local state immediately so the sidebar
 * doesn't snap back to the old server value, then persist to the server via
 * `updateDockedPanelWidth`.
 */
export function useDockedPanelWidthControl({
  serverWidth,
  minWidth,
  maxWidth,
  owner,
  repo,
  pullNumber,
}: UseDockedPanelWidthControlParams): UseDockedPanelWidthControlResult {
  const [currentWidth, setCurrentWidth] = useState<number | undefined>(() => clamp(serverWidth, minWidth, maxWidth))

  // Re-sync local state when the server value changes (refetch / route change).
  const [lastServerWidth, setLastServerWidth] = useState<number | null | undefined>(serverWidth)
  if (lastServerWidth !== serverWidth) {
    setLastServerWidth(serverWidth)
    setCurrentWidth(clamp(serverWidth, minWidth, maxWidth))
  }

  const onResizeEnd = useCallback(
    (newWidth: number) => {
      if (!Number.isFinite(newWidth)) return
      if (newWidth < minWidth || newWidth > maxWidth) return
      if (newWidth === currentWidth) return

      setCurrentWidth(newWidth)

      if (!owner || !repo || !pullNumber) return
      if (!isLoggedIn()) return

      updateDockedPanelWidth(newWidth, owner, repo, pullNumber)
    },
    [currentWidth, minWidth, maxWidth, owner, repo, pullNumber],
  )

  return {currentWidth, onResizeEnd}
}
